import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressSearchWrapper } from '../../inputs/AddressSearchInputs/index.styled'
import AutocompleteInput from '../../inputs/AutocompleteInput'
import TextInput from '../../inputs/TextInput'
import { autocompleteHereThunkSilent } from '../../../../redux/thunk/geoCode/here-api'
import { filterAutocompleteSearchResult } from '../../inputs/AddressSearchInputs/helper'
import { useDispatch, useSelector } from 'react-redux'
import { selectCompanyData } from '../../../../redux/selector/system'
import { setAppEventZIndex } from '../../../../redux/action/system'
import { setAutocompleteGeo, setCustomerAddressIsNotFull } from '../../../../redux/action/customer'

const EVENT_Z_INDEX = 114

const AddressFields = ({
	dataName,
	register,
	errors,
	setValue,
	watch,
	clearErrors,
	firstTabIndex,
	secondTabIndex,
	thirdTbaIndex,
	fourthTabIndex,
	setFocus,
	autocompleteRequest,
	geoDataPermissionIsNeeded = false,
	countryCode,
	validationIsRequired = true,
	size,
	color,
	textArea = false,
	disabled,
	returnZIndex = 0,
}) => {
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const [addressItems, setAddressItems] = useState([])
	const [browserLocation, setBrowserLocation] = useState(null)
	const companyLatitude = useSelector(selectCompanyData('latitude'))
	const companyLongitude = useSelector(selectCompanyData('longitude'))
	const companyLocation = (companyLatitude && companyLongitude) ? `${companyLatitude},${companyLongitude}` : null
	const customerAddressIsNotFull = useSelector(store => store.customerStore.customerAddressIsNotFull)
	const watchDataNameHouse = watch(`${dataName}.house`)
	const watchDataNameStreet = watch(`${dataName}.street`)
	const watchDataNameZipCode = watch(`${dataName}.zipCode`)
	const watchDataNameCity = watch(`${dataName}.city`)

	useEffect(() => {
		if (addressItems && addressItems.length > 0) {
			dispatch(setAppEventZIndex(EVENT_Z_INDEX))
		}
		return () => {
			dispatch(setAppEventZIndex(returnZIndex))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!addressItems?.length])

	useEffect(() => {
		if (geoDataPermissionIsNeeded && navigator.geolocation) {
			navigator.geolocation.getCurrentPosition((position) => {
				setBrowserLocation(`${position.coords.latitude},${position.coords.longitude}`)
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (customerAddressIsNotFull && (watchDataNameCity || watchDataNameStreet || watchDataNameZipCode)) {
			dispatch(autocompleteHereThunkSilent(countryCode, companyLocation, watchDataNameStreet + ' ' + (watchDataNameZipCode ? watchDataNameZipCode : '') + '  ' + (watchDataNameHouse ? watchDataNameHouse : ''), autocompleteRequest, res => {
				const filteredData = filterAutocompleteSearchResult(res)
				if (filteredData?.length === 1) {
					clickOnAutocompleteAddress(filteredData[0])
				} else {
					setAddressItems(filteredData)
				}
			}))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerAddressIsNotFull, watchDataNameStreet, watchDataNameHouse])

	const handleAddressInput = value => {
		clearErrors()
		dispatch(autocompleteHereThunkSilent(countryCode, companyLocation ? companyLocation : browserLocation, value + (watchDataNameHouse ? ' ' + watchDataNameHouse : ''), autocompleteRequest, res => {
			const filteredData = filterAutocompleteSearchResult(res)
			setAddressItems(filteredData)
		}))
	}

	const clickOnAutocompleteAddress = async (data) => {
		if (data.address.houseNumber) {
			setValue(`${dataName}.house`, data.address.houseNumber)
		}
		setValue(`${dataName}.zipCode`, data.address.postalCode)
		setValue(`${dataName}.city`, data.address.city)
		setValue(`${dataName}.street`, data.address.street)

		dispatch(setCustomerAddressIsNotFull(false))
		setFocus(`${dataName}.house`)
		setAddressItems([])
		dispatch(setAutocompleteGeo(false))
	}

	const handleEscape = useCallback(() => {
		setAddressItems([])
	}, [])

	const handleManualInput = (field, e) => {
		clearErrors()
		const value = e.target.value
		const valueLowerCase = value.toLowerCase()
		setValue(field, valueLowerCase.trim())
	}
	const handleManualInputCity = (e) => {
		clearErrors()
		const value = e.target.value
		setValue(`${dataName}.city`, value.trim())
	}

	const validateAddress = useCallback(() => {
		if (!watchDataNameStreet) return t('app.validation.street')
		return true
	},  [watchDataNameStreet, t])


	const validateHouseNumber = useCallback(() => {
		if (!watchDataNameHouse) return t('app.validation.streetNumber')
		return true
	}, [watchDataNameHouse, t])

	const validateZipCode = useCallback(() => {
		if (!watchDataNameZipCode) return t('app.validation.postalCode')
		return true
	}, [watchDataNameZipCode, t])

	const validateCity = useCallback(() => {
		if (!watchDataNameCity) return t('app.validation.city')
		return true
	}, [watchDataNameCity, t])

	return (
		<AddressSearchWrapper className="cols-2">
			<AutocompleteInput
				testId={`${dataName}.street`}
				name={`${dataName}.street`}
				label={t('AddOrder.Client.form.Strasse') + '*'}
				errors={errors}
				errorParser={errors => errors?.[dataName] && errors?.[dataName].street}
				register={register}
				options={{
					required: t('app.validation.required'),
					validate: validationIsRequired && validateAddress,
				}}
				items={addressItems}
				Item={({ item }) => <p>{item?.address?.label}</p>}
				watch={watchDataNameStreet}
				onAutoComplete={handleAddressInput}
				onClick={clickOnAutocompleteAddress}
				setItems={(value) => setAddressItems(value)}
				onReset={() => handleEscape}
				tabIndex={firstTabIndex}
				color={color}
				size={size}
				textArea={textArea}
				disabled={disabled}
			/>
			<TextInput
				testId={`${dataName}.house`}
				name={`${dataName}.house`}
				label={t('AddOrder.Client.form.Hausnummer') + '*'}
				errors={errors}
				errorParser={errors => errors?.[dataName] && errors?.[dataName].house}
				register={register}
				options={{
					required: t('app.validation.required'),
					validate: validationIsRequired && validateHouseNumber,
				}}
				watch={watchDataNameHouse}
				onChange={(e) => handleManualInput(`${dataName}.house`, e)}
				tabIndex={secondTabIndex}
				color={color}
				size={size}
				disabled={disabled}
			/>
			<TextInput
				testId={`${dataName}.city`}
				name={`${dataName}.city`}
				label={t('AddOrder.Client.form.City') + '*'}
				errors={errors}
				errorParser={errors => errors?.[dataName] && errors?.[dataName].city}
				tabIndex={thirdTbaIndex}
				register={register}
				options={{
					required: t('app.validation.required'),
					validate: validationIsRequired && validateCity,
				}}
				watch={watchDataNameCity}
				onChange={handleManualInputCity}
				color={color}
				size={size}
				disabled={disabled}
			/>
			<TextInput
				testId={`${dataName}.zipCode`}
				name={`${dataName}.zipCode`}
				label={t('AddOrder.Client.form.PostalCode') + '*'}
				errors={errors}
				errorParser={errors => errors?.[dataName] && errors?.[dataName].zipCode}
				register={register}
				options={{
					required: t('app.validation.required'),
					validate: validationIsRequired && validateZipCode,
				}}
				watch={watchDataNameZipCode}
				onChange={(e) => handleManualInput(`${dataName}.zipCode`, e)}
				tabIndex={fourthTabIndex}
				color={color}
				size={size}
				disabled={disabled}
			/>
		</AddressSearchWrapper>
	)
}

AddressFields.displayName = 'AddressFields'
export default AddressFields
